import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Loader2, Trash2, LayoutGrid, CheckCircle2, XCircle, AlertCircle, X } from 'lucide-react';
import ListingSearchInput from './ListingSearchInput';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

// iter-386 — Universal Catalog Listing, Phase 1 foundation.
// Children are always references to EXISTING listings — this card never
// stores price/stock here, only composition metadata (quantity rules,
// required flag, position). See /app/memory/catalog_listing_audit_report.md
// §10.1-10.3.
//
// Catalog Architecture v7 Phase F1 (Bundle/Package seller UX) additions:
// - Staged multi-select: the search box accumulates a selection (no API
//   calls yet); a single "Add N selected items" button commits them all.
// - Eligibility filter: only the vendor's own active/draft listings are
//   searchable here (enforced server-side in both search-autocomplete's
//   `statuses` param AND, authoritatively, in the backend
//   `add_catalog_item` endpoint's own status check — UI filtering alone
//   is never the sole guard).
// - Partial-failure reporting: every staged item ends up in exactly one
//   bucket (added / already in catalog / failed) — nothing is silently
//   dropped or duplicated.
// - Selection state lives in this component's own React state, which is
//   not remounted when sibling form fields (e.g. the catalog title)
//   change — selections survive title/description edits for free.
const ELIGIBLE_STATUSES = ['active', 'draft'];

export const CatalogItemsCard = ({ listingId, onSaveDraftClick, saveDraftLoading = false }) => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [stagedSelection, setStagedSelection] = useState([]);
  const [adding, setAdding] = useState(false);
  const [addResults, setAddResults] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!listingId) return;
    setLoading(true);
    try {
      const res = await apiCall(`/api/listings/${listingId}/catalog-items`);
      setItems(Array.isArray(res) ? res : []);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to load catalog items', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [listingId, apiCall, toast]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Commits the staged selection via the existing per-item endpoint, one
  // call per item, in sequence — no new bulk endpoint (per Catalog
  // Architecture v6 §5.2 / v7 Part F1 decision: the existing endpoint is
  // fast enough at a bundle's realistic component count). Every item ends
  // in exactly one of three buckets so nothing is lost or duplicated.
  const handleAddSelected = async () => {
    if (stagedSelection.length === 0 || adding) return;
    setAdding(true);
    const existingIds = new Set(items.map((i) => i.component_listing_id));
    const results = { succeeded: [], skipped: [], failed: [] };
    let nextPosition = items.length;

    for (const listing of stagedSelection) {
      if (existingIds.has(listing.id)) {
        results.skipped.push({ ...listing, reason: 'Already in this catalog' });
        continue;
      }
      try {
        await apiCall(`/api/listings/${listingId}/catalog-items`, {
          method: 'POST',
          body: JSON.stringify({ component_listing_id: listing.id, position: nextPosition }),
        });
        results.succeeded.push(listing);
        existingIds.add(listing.id);
        nextPosition += 1;
      } catch (error) {
        const msg = error.message || 'Could not add this item';
        if (msg.toLowerCase().includes('already in the catalog')) {
          results.skipped.push({ ...listing, reason: 'Already in this catalog' });
        } else {
          results.failed.push({ ...listing, reason: msg });
        }
      }
    }

    setAddResults(results);
    setStagedSelection([]);
    await fetchItems();
    setAdding(false);

    const parts = [];
    if (results.succeeded.length) parts.push(`${results.succeeded.length} added`);
    if (results.skipped.length) parts.push(`${results.skipped.length} already in catalog`);
    if (results.failed.length) parts.push(`${results.failed.length} failed`);
    toast({
      title: results.failed.length ? 'Added with some issues' : 'Items added',
      description: parts.join(', ') || 'Nothing to add',
      variant: results.failed.length ? 'destructive' : 'default',
    });
  };

  const handleUpdateItem = async (item, patch) => {
    setSavingId(item.id);
    try {
      await apiCall(`/api/listings/${listingId}/catalog-items/${item.id}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, ...patch } : i)));
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to update item', variant: 'destructive' });
    } finally {
      setSavingId(null);
    }
  };

  const handleRemoveItem = async (item) => {
    try {
      await apiCall(`/api/listings/${listingId}/catalog-items/${item.id}`, { method: 'DELETE' });
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast({ title: 'Removed', description: 'Item removed from catalog.' });
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to remove item', variant: 'destructive' });
    }
  };

  if (!listingId) {
    return (
      <Card data-testid="catalog-items-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <LayoutGrid className="h-4 w-4" /> Catalog Items
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-500" data-testid="catalog-items-save-first-note">
            Save this catalog first, then come back here to add the listings it contains.
          </p>
          {onSaveDraftClick && (
            <Button
              type="button" size="sm" onClick={onSaveDraftClick} disabled={saveDraftLoading}
              data-testid="catalog-items-save-draft-btn"
            >
              {saveDraftLoading && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Save Draft to Continue
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="catalog-items-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <LayoutGrid className="h-4 w-4" /> Catalog Items
        </CardTitle>
        <CardDescription>
          Add the listings this catalog contains — e.g. each room type, menu item, or bundle
          component. Price, stock, tax and compliance always come from the item's own listing.
          Draft listings of yours are selectable; archived or suspended listings are not.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <ListingSearchInput
            value={stagedSelection}
            onChange={setStagedSelection}
            placeholder="Search your listings to add to this catalog..."
            currentListingId={listingId}
            statusFilter={ELIGIBLE_STATUSES}
            helperText="Select one or more listings, then click Add selected items."
          />
          <Button
            type="button"
            size="sm"
            onClick={handleAddSelected}
            disabled={stagedSelection.length === 0 || adding}
            data-testid="catalog-add-selected-btn"
            className="w-full sm:w-auto"
          >
            {adding ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : null}
            Add {stagedSelection.length > 0 ? stagedSelection.length : ''} selected item{stagedSelection.length === 1 ? '' : 's'}
          </Button>
        </div>

        {addResults && (
          <div
            className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1.5 text-sm"
            data-testid="catalog-add-results-banner"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-700">Last add result</span>
              <button
                type="button"
                onClick={() => setAddResults(null)}
                className="text-gray-400 hover:text-gray-600"
                data-testid="catalog-add-results-dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            {addResults.succeeded.map((r) => (
              <div key={`ok-${r.id}`} className="flex items-center gap-1.5 text-green-700" data-testid={`catalog-add-result-success-${r.id}`}>
                <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{r.title} — added</span>
              </div>
            ))}
            {addResults.skipped.map((r) => (
              <div key={`skip-${r.id}`} className="flex items-center gap-1.5 text-amber-700" data-testid={`catalog-add-result-skipped-${r.id}`}>
                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{r.title} — {r.reason}</span>
              </div>
            ))}
            {addResults.failed.map((r) => (
              <div key={`fail-${r.id}`} className="flex items-center gap-1.5 text-red-700" data-testid={`catalog-add-result-failed-${r.id}`}>
                <XCircle className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{r.title} — {r.reason}</span>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-6 text-gray-400" data-testid="catalog-items-loading">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="catalog-items-empty">
            No items added yet. Search above to add your first item.
          </p>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex flex-wrap items-center gap-3 border rounded-lg p-3"
                data-testid={`catalog-item-row-${item.id}`}
              >
                <div className="flex-1 min-w-[160px] font-medium text-sm" data-testid={`catalog-item-title-${item.id}`}>
                  {item.component_listing_title || item.component_listing_id}
                </div>
                <div className="flex items-center gap-1.5">
                  <label className="text-xs text-gray-500">Default qty</label>
                  <Input
                    type="number"
                    min={0}
                    className="h-8 w-16 text-xs"
                    defaultValue={item.default_quantity}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value, 10) || 0;
                      if (v !== item.default_quantity) handleUpdateItem(item, { default_quantity: v });
                    }}
                    data-testid={`catalog-item-qty-${item.id}`}
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <Checkbox
                    id={`catalog-item-required-${item.id}`}
                    checked={!!item.is_required}
                    onCheckedChange={(v) => handleUpdateItem(item, { is_required: !!v })}
                    data-testid={`catalog-item-required-${item.id}`}
                  />
                  <label htmlFor={`catalog-item-required-${item.id}`} className="text-xs text-gray-500 cursor-pointer">
                    Required
                  </label>
                </div>
                {savingId === item.id && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleRemoveItem(item)}
                  data-testid={`catalog-item-remove-${item.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CatalogItemsCard;
